/**
 * Mobile Navigation Component
 * 
 * Provides a mobile-friendly navigation with hamburger menu and overlay.
 * Ensures touch targets meet WCAG 2.1 Level AA requirements (44x44px minimum).
 */

import React, { useEffect } from 'react';
import { Button } from '@cloudscape-design/components';
import { SideNavigation } from './SideNavigation';
import type { SideNavigationProps as CloudscapeSideNavigationProps } from '@cloudscape-design/components';
import './MobileNavigation.css';

export interface MobileNavigationProps {
  activeHref: string;
  onFollow: (event: CustomEvent<CloudscapeSideNavigationProps.FollowDetail>) => void;
  isOpen: boolean;
  onToggle: (open: boolean) => void;
}

export const MobileNavigation: React.FC<MobileNavigationProps> = ({
  activeHref,
  onFollow,
  isOpen,
  onToggle,
}) => {
  // Close navigation when route changes
  useEffect(() => {
    onToggle(false);
  }, [activeHref, onToggle]);

  // Prevent body scroll when navigation is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key to close navigation
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onToggle(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onToggle]);

  const handleOverlayClick = () => {
    onToggle(false);
  };

  const handleNavigationFollow = (event: CustomEvent<CloudscapeSideNavigationProps.FollowDetail>) => {
    onFollow(event);
    onToggle(false);
  };

  return (
    <>
      {/* Hamburger Menu Button */}
      <Button
        iconName="menu"
        variant="icon"
        onClick={() => onToggle(!isOpen)}
        ariaLabel={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
        ariaExpanded={isOpen}
        className="mobile-nav-toggle"
      />

      {/* Overlay */}
      {isOpen && (
        <div
          className="mobile-nav-overlay"
          onClick={handleOverlayClick}
          role="presentation"
          aria-hidden="true"
        />
      )}

      {/* Navigation Drawer */}
      <aside
        className={`mobile-nav-drawer ${isOpen ? 'open' : ''}`}
        role="navigation"
        aria-label="Mobile navigation"
        aria-hidden={!isOpen}
      >
        <div className="mobile-nav-header">
          <h2>Menu</h2>
          <Button
            iconName="close"
            variant="icon"
            onClick={() => onToggle(false)}
            ariaLabel="Close navigation menu"
            className="mobile-nav-close"
          />
        </div>
        <div className="mobile-nav-content">
          <SideNavigation activeHref={activeHref} onFollow={handleNavigationFollow} />
        </div>
      </aside>
    </>
  );
};
